import React, { memo, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';
import cn from 'classnames';

import ReadOnlyFieldTooltip from '../helpers/ReadOnlyFieldTooltip';
import buildComplianceFieldLabel from '../helpers/buildComplianceFieldLabel';
import { useLocalFieldValue } from '../hooks';
import { buildSeoFieldHelperText, buildSeoFallbackTooltip } from './helpers/buildSeoFieldHelperText';
import styles from '../styles.module.scss';

const SeoField = ({
  label,
  value,
  readOnly,
  readOnlyTooltip,
  isDiff,
  recommendedMax,
  field,
  fallbackValue,
  multiline,
  onChange,
}) => {
  const { localValue, setLocalValue } = useLocalFieldValue(value);

  const isRecommendedLengthExceeded = localValue.length > recommendedMax;

  const helperText = useMemo(() => buildSeoFieldHelperText({
    value: localValue,
    recommendedMax,
    field,
  }), [localValue, recommendedMax, field]);

  const fallbackTooltip = useMemo(() => buildSeoFallbackTooltip({
    value: localValue,
    field,
    fallbackValue,
  }), [localValue, field, fallbackValue]);

  const fieldLabel = useMemo(() => buildComplianceFieldLabel({
    label,
    diff: isDiff,
    infoTooltip: fallbackTooltip,
  }), [label, isDiff, fallbackTooltip]);

  const handleChange = useCallback((event) => {
    if (readOnly) return;
    setLocalValue(event.target.value);
  }, [readOnly, setLocalValue]);

  const handleBlur = useCallback(() => {
    if (readOnly) return;
    const nextValue = localValue.trim();
    if (nextValue !== localValue) setLocalValue(nextValue);
    if (nextValue !== value) onChange?.(nextValue);
  }, [localValue, onChange, readOnly, setLocalValue, value]);

  const handleKeyPress = useCallback((event) => {
    if (!multiline && event.key === 'Enter') event.target.blur();
  }, [multiline]);

  return (
    <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
      <div className={cn(styles.row, styles.seoFields, isRecommendedLengthExceeded && styles.seoFieldsWarning)}>
        <TextField
          value={localValue}
          disabled={readOnly}
          label={fieldLabel}
          helperText={helperText}
          InputProps={multiline ? {
            component: 'textarea',
            multiline: true,
            rows: 4,
          } : undefined}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
        />
      </div>
    </ReadOnlyFieldTooltip>
  );
};

SeoField.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  isDiff: PropTypes.bool,
  recommendedMax: PropTypes.number.isRequired,
  field: PropTypes.oneOf(['title', 'description']).isRequired,
  fallbackValue: PropTypes.string,
  multiline: PropTypes.bool,
  onChange: PropTypes.func,
};

SeoField.defaultProps = {
  value: '',
  readOnly: false,
  readOnlyTooltip: null,
  isDiff: false,
  fallbackValue: '',
  multiline: false,
  onChange: null,
};

export default memo(SeoField);
